/**
 * @module Constants
 */

const CENTER_OF_USA = { lat: 37.0902, lng: -95.7129 };

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// Window Breakpoints
const BREAKPOINTS = {
  large: 1024,
  medium: 768,
  small: 640,
  xLarge: 1200,
};

// Truncate Delimiters
const DELIMITERS = ['.', '?', '!', ',', ';', ':', '—', '–', '-'];

// Status Types
const STATUS_TYPES = {
  ERROR: 'error',
  IDLE: 'idle',
  INFO: 'info',
  PENDING: 'pending',
  REJECTED: 'rejected',
  RESOLVED: 'resolved',
  SUCCESS: 'success',
  WARNING: 'warning',
};

// Authentication
const AUTH0 = Object.freeze({
  callbacks: {
    path: '/auth/callback',
  },
  cookies: {
    accessToken: 'access_token',
    authorizeState: 'auth0-authorize-state',
    expiresAt: 'expires_at',
  },
  errors: {
    accountDisabled:
      'Sorry, but this account has been disabled. Please contact us at help@life.church for assistance.',
    loginError:
      'Sorry, but there was an error logging you in. Please contact us at help@life.church for assistance.',
  },
  events: {
    accountLoggedIn: 'Account Logged In',
    accountLoggedInError: 'Account Logged In Error',
    accountLoggedOut: 'Account Logged Out',
  },
  localStorageKeys: {
    accessToken: 'access_token',
    authorizeState: 'auth0-authorize-state',
    errorAlert: 'error-alert',
    expiresAt: 'expires_at',
    givingEntrance: 'giving_entrance',
    userGivingData: 'LC:WebGiving:userGivingData',
    userProfile: 'user_profile',
  },
  profile: {
    avatarImgAlt: 'My Profile',
    userMetadataUrl: 'https://www.life.church/user_metadata',
  },
});

// Element Attribute Props
const GLOBAL_SUPPORTED_ATTRIBUTES = [
  'accesskey',
  'autocapitalize',
  'autofocus',
  'class',
  'className',
  'contenteditable',
  'dir',
  'draggable',
  'enterkeyhint',
  'exportparts',
  'hidden',
  'id',
  'inert',
  'inputmode',
  'is',
  'itemid',
  'itemprop',
  'itemref',
  'itemscope',
  'itemtype',
  'lang',
  'nonce',
  'part',
  'popover',
  'role',
  'slot',
  'spellcheck',
  'style',
  'tabindex',
  'title',
  'translate',
  'virtualkeyboardpolicy',
];

const SUPPORTED_ATTRIBUTES = {
  a: [
    'download',
    'href',
    'hreflang',
    'ping',
    'referrerpolicy',
    'rel',
    'target',
    'type',
  ],
  area: [
    'alt',
    'coords',
    'download',
    'href',
    'ping',
    'referrerpolicy',
    'rel',
    'shape',
    'target',
  ],
  button: [
    'autofocus',
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'name',
    'popovertarget',
    'popovertargetaction',
    'type',
    'value',
  ],
  div: GLOBAL_SUPPORTED_ATTRIBUTES,
  form: [
    'accept',
    'accept-charset',
    'action',
    'autocapitalize',
    'autocomplete',
    'enctype',
    'method',
    'name',
    'novlidate',
    'rel',
    'target',
  ],
  global: GLOBAL_SUPPORTED_ATTRIBUTES,
  iframe: [
    'allow',
    'allowfullscreen',
    'allowpaymentrequest',
    'browsingtopics',
    'credentialless',
    'csp',
    'height',
    'loading',
    'name',
    'referrerpolicy',
    'sandbox',
    'src',
    'srcdoc',
    'width',
  ],
  img: [
    'alt',
    'crossorigin',
    'decoding',
    'elementtiming',
    'fetchpriority',
    'height',
    'ismap',
    'loading',
    'referrerpolicy',
    'sizes',
    'src',
    'srcset',
    'width',
    'usemap',
  ],
  input: [
    'accept',
    'alt',
    'autocapitalize',
    'autocomplete',
    'capture',
    'checked',
    'dirname',
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'height',
    'list',
    'max',
    'maxlength',
    'min',
    'minlength',
    'multiple',
    'name',
    'pattern',
    'placeholder',
    'popovertarget',
    'popovertargetaction',
    'readonly',
    'required',
    'size',
    'src',
    'step',
    'type',
    'value',
    'width',
  ],
  label: ['for'],
  li: ['type', 'value'],
  ol: ['reversed', 'start', 'type'],
  optgroup: ['disabled', 'label'],
  options: ['disabled', 'label', 'selected', 'value'],
  select: [
    'autocomplete',
    'autofocus',
    'disabled',
    'form',
    'multiple',
    'name',
    'required',
    'size',
  ],
  table: GLOBAL_SUPPORTED_ATTRIBUTES,
  textarea: [
    'autocapitalize',
    'autocomplete',
    'autocorrect',
    'autofocus',
    'cols',
    'dirname',
    'disabled',
    'form',
    'maxlength',
    'minlength',
    'name',
    'placeholder',
    'readonly',
    'required',
    'rows',
    'spellcheck',
    'wrap',
  ],
  ul: ['compact', 'type'],
  video: [
    'autoplay',
    'controls',
    'controlslist',
    'crossorigin',
    'disablepictureinpicture',
    'disableremoteplayback',
    'height',
    'loop',
    'muted',
    'playsinline',
    'poster',
    'preload',
    'src',
    'width',
  ],
};

// Magnolia
const MAGNOLIA_ENDPOINT_WORKSPACE_MAP = Object.freeze({
  '/.rest/delivery/careersMenu': 'careersMenu',
  '/.rest/delivery/distributionPlatform': 'distributionPlatform',
  '/.rest/delivery/featuredNextStepCard': 'featuredNextStepCard',
  '/.rest/delivery/finds/footer': 'finds-footer',
  '/.rest/delivery/finds/journey': 'finds-journey',
  '/.rest/delivery/finds/menu': 'finds-menu',
  '/.rest/delivery/finds/share': 'finds-share',
  '/.rest/delivery/finds-articles/v1': 'finds-articles',
  '/.rest/delivery/finds-pagenav/v1': 'finds-website',
  '/.rest/delivery/finds-pages/v1': 'finds-website',
  '/.rest/delivery/footer': 'footer',
  '/.rest/delivery/globalLocationBanner': 'globalLocationBanner',
  '/.rest/delivery/homepageHero': 'homepageHero',
  '/.rest/delivery/labeldictionary': 'labeldictionary',
  '/.rest/delivery/location': 'location',
  '/.rest/delivery/mediaPerson': 'mediaPerson',
  '/.rest/delivery/mediav2': 'media_v2',
  '/.rest/delivery/mediaV2': 'media_v2',
  '/.rest/delivery/menu': 'menu',
  '/.rest/delivery/messageNotes': 'messageNotes',
  '/.rest/delivery/modal': 'modal',
  '/.rest/delivery/nextStepCard': 'nextStepCard',
  '/.rest/delivery/pagenav/v1': 'website',
  '/.rest/delivery/pages/v1': 'website',
  '/.rest/delivery/servingOpportunities': 'servingOpportunities',
  '/.rest/delivery/specialEvent': 'specialEvent',
  '/.rest/delivery/tag': 'tag',
  '/.rest/delivery/talkItOver': 'talkItOver',
  '/.rest/delivery/tasks': 'tasks',
  '/.rest/delivery/v1/collections': 'media_v2',
  '/.rest/delivery/v1/items': 'media_v2',
  '/.rest/mediaV2': 'media_v2',
  mediaV2: 'media_v2',
  mediav2: 'media_v2',
});

// U.S. States
const US_STATES = [
  {
    code: 'AK',
    lat: '63.588753',
    lng: '-154.493062',
    name: 'Alaska',
  },
  {
    code: 'AL',
    lat: '32.318231',
    lng: '-86.902298',
    name: 'Alabama',
  },
  {
    code: 'AR',
    lat: '35.20105',
    lng: '-91.831833',
    name: 'Arkansas',
  },
  {
    code: 'AZ',
    lat: '34.048928',
    lng: '-111.093731',
    name: 'Arizona',
  },
  {
    code: 'CA',
    lat: '36.778261',
    lng: '-119.417932',
    name: 'California',
  },
  {
    code: 'CO',
    lat: '39.550051',
    lng: '-105.782067',
    name: 'Colorado',
  },
  {
    code: 'CT',
    lat: '41.603221',
    lng: '-73.087749',
    name: 'Connecticut',
  },
  {
    code: 'DC',
    lat: '38.905985',
    lng: '-77.033418',
    name: 'District of Columbia',
  },
  {
    code: 'DE',
    lat: '38.910832',
    lng: '-75.52767',
    name: 'Delaware',
  },
  {
    code: 'FL',
    lat: '27.664827',
    lng: '-81.515754',
    name: 'Florida',
  },
  {
    code: 'GA',
    lat: '32.157435',
    lng: '-82.907123',
    name: 'Georgia',
  },
  {
    code: 'HI',
    lat: '19.898682',
    lng: '-155.665857',
    name: 'Hawaii',
  },
  {
    code: 'IA',
    lat: '41.878003',
    lng: '-93.097702',
    name: 'Iowa',
  },
  {
    code: 'ID',
    lat: '44.068202',
    lng: '-114.742041',
    name: 'Idaho',
  },
  {
    code: 'IL',
    lat: '40.633125',
    lng: '-89.398528',
    name: 'Illinois',
  },
  {
    code: 'IN',
    lat: '40.551217',
    lng: '-85.602364',
    name: 'Indiana',
  },
  {
    code: 'KS',
    lat: '38.011902',
    lng: '-98.484246',
    name: 'Kansas',
  },
  {
    code: 'KY',
    lat: '37.839333',
    lng: '-84.270018',
    name: 'Kentucky',
  },
  {
    code: 'LA',
    lat: '31.244823',
    lng: '-92.145024',
    name: 'Louisiana',
  },
  {
    code: 'MA',
    lat: '42.407211',
    lng: '-71.382437',
    name: 'Massachusetts',
  },
  {
    code: 'MD',
    lat: '39.045755',
    lng: '-76.641271',
    name: 'Maryland',
  },
  {
    code: 'ME',
    lat: '45.253783',
    lng: '-69.445469',
    name: 'Maine',
  },
  {
    code: 'MI',
    lat: '44.314844',
    lng: '-85.602364',
    name: 'Michigan',
  },
  {
    code: 'MN',
    lat: '46.729553',
    lng: '-94.6859',
    name: 'Minnesota',
  },
  {
    code: 'MO',
    lat: '37.964253',
    lng: '-91.831833',
    name: 'Missouri',
  },
  {
    code: 'MS',
    lat: '32.354668',
    lng: '-89.398528',
    name: 'Mississippi',
  },
  {
    code: 'MT',
    lat: '46.879682',
    lng: '-110.362566',
    name: 'Montana',
  },
  {
    code: 'NC',
    lat: '35.759573',
    lng: '-79.0193',
    name: 'North Carolina',
  },
  {
    code: 'ND',
    lat: '47.551493',
    lng: '-101.002012',
    name: 'North Dakota',
  },
  {
    code: 'NE',
    lat: '41.492537',
    lng: '-99.901813',
    name: 'Nebraska',
  },
  {
    code: 'NH',
    lat: '43.193852',
    lng: '-71.572395',
    name: 'New Hampshire',
  },
  {
    code: 'NJ',
    lat: '40.058324',
    lng: '-74.405661',
    name: 'New Jersey',
  },
  {
    code: 'NM',
    lat: '34.97273',
    lng: '-105.032363',
    name: 'New Mexico',
  },
  {
    code: 'NV',
    lat: '38.80261',
    lng: '-116.419389',
    name: 'Nevada',
  },
  {
    code: 'NY',
    lat: '43.299428',
    lng: '-74.217933',
    name: 'New York',
  },
  {
    code: 'OH',
    lat: '40.417287',
    lng: '-82.907123',
    name: 'Ohio',
  },
  {
    code: 'OK',
    lat: '35.007752',
    lng: '-97.092877',
    name: 'Oklahoma',
  },
  {
    code: 'OR',
    lat: '43.804133',
    lng: '-120.554201',
    name: 'Oregon',
  },
  {
    code: 'PA',
    lat: '41.203322',
    lng: '-77.194525',
    name: 'Pennsylvania',
  },
  {
    code: 'PR',
    lat: '18.220833',
    lng: '-66.590149',
    name: 'Puerto Rico',
  },
  {
    code: 'RI',
    lat: '41.580095',
    lng: '-71.477429',
    name: 'Rhode Island',
  },
  {
    code: 'SC',
    lat: '33.836081',
    lng: '-81.163725',
    name: 'South Carolina',
  },
  {
    code: 'SD',
    lat: '43.969515',
    lng: '-99.901813',
    name: 'South Dakota',
  },
  {
    code: 'TN',
    lat: '35.517491',
    lng: '-86.580447',
    name: 'Tennessee',
  },
  {
    code: 'TX',
    lat: '31.968599',
    lng: '-99.901813',
    name: 'Texas',
  },
  {
    code: 'UT',
    lat: '39.32098',
    lng: '-111.093731',
    name: 'Utah',
  },
  {
    code: 'VA',
    lat: '37.431573',
    lng: '-78.656894',
    name: 'Virginia',
  },
  {
    code: 'VT',
    lat: '44.558803',
    lng: '-72.577841',
    name: 'Vermont',
  },
  {
    code: 'WA',
    lat: '47.751074',
    lng: '-120.740139',
    name: 'Washington',
  },
  {
    code: 'WI',
    lat: '43.78444',
    lng: '-88.787868',
    name: 'Wisconsin',
  },
  {
    code: 'WV',
    lat: '38.597626',
    lng: '-80.454903',
    name: 'West Virginia',
  },
  {
    code: 'WY',
    lat: '43.075968',
    lng: '-107.290284',
    name: 'Wyoming',
  },
];

module.exports = {
  AUTH0,
  BREAKPOINTS,
  CENTER_OF_USA,
  DAYS_OF_WEEK,
  DELIMITERS,
  GLOBAL_SUPPORTED_ATTRIBUTES,
  MAGNOLIA_ENDPOINT_WORKSPACE_MAP,
  STATUS_TYPES,
  SUPPORTED_ATTRIBUTES,
  US_STATES,
};
